import { useState } from 'react';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ModalV2, ButtonV2 } from '@bloomays-lib/ui.shared';
import { LostReasonType, type IMissionCardDetails } from '@bloomays-lib/types.shared';
import { ALL_MISSIONS, DELETE_MISSION } from '@bloomays-lib/adapter.api-bloomer';

import { notify } from '../../../../helpers/toastify';
import { FieldV2, InputV2, SelectV2, InfoV2 } from '../formFields';

export type DeleteModalProps = {
  open: boolean;
  onClose: () => void;
  mission: IMissionCardDetails;
};

/** Supprimer la mission (ModalV2) — refonte V2 du flux de suppression (`DELETE_MISSION`). */
export const DeleteModal = ({ open, onClose, mission }: DeleteModalProps) => {
  const { t } = useTranslation(['mission', 'random']);
  const navigate = useNavigate();

  const [lostReason, setLostReason] = useState<LostReasonType | ''>('');
  const [otherLostReason, setOtherLostReason] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const [deleteMissionMutation, { loading }] = useMutation(DELETE_MISSION, {
    refetchQueries: [{ query: ALL_MISSIONS, variables: { isActive: false } }],
  });

  const otherMissing = lostReason === LostReasonType.autre && otherLostReason.trim().length === 0;
  const confirmOk = confirmText.trim().toUpperCase() === 'SUPPRIMER';
  const disabled = loading || lostReason === '' || otherMissing || !confirmOk;

  const del = async () => {
    if (lostReason === '') return;
    try {
      await deleteMissionMutation({
        variables: {
          missionId: mission.recordId,
          lostReason,
          otherLostReason: lostReason === LostReasonType.autre ? otherLostReason.trim() : undefined,
        },
      });
      navigate('/');
    } catch (e: any) {
      notify('error', e.message);
    }
  };

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      icon={<DeleteRoundedIcon sx={{ fontSize: 18 }} />}
      iconTone="rouge"
      title="Supprimer la mission"
      width={480}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            {t('cancel', { ns: 'random' })}
          </ButtonV2>
          <ButtonV2
            variant="danger"
            disabled={disabled}
            loading={loading}
            startIcon={<DeleteRoundedIcon sx={{ fontSize: 15 }} />}
            onClick={del}
          >
            Supprimer définitivement
          </ButtonV2>
        </>
      }
    >
      <InfoV2 tone="amber" icon={<WarningAmberRoundedIcon sx={{ fontSize: 16 }} />}>
        {t('deleteWarning')}
      </InfoV2>
      <FieldV2 label={t('deleteLostReasonLabel')} required>
        <SelectV2
          value={lostReason}
          placeholder={t('deleteLostReasonNone')}
          onChange={(raw) => {
            if (raw === '') {
              setLostReason('');
              setOtherLostReason('');
              return;
            }
            const next = raw as LostReasonType;
            setLostReason(next);
            if (next !== LostReasonType.autre) setOtherLostReason('');
          }}
          options={[
            { value: LostReasonType.client_annule_post_win, label: t('deleteLostReasonClientAnnule') },
            { value: LostReasonType.bloomer_annule_post_win, label: t('deleteLostReasonBloomerAnnule') },
            { value: LostReasonType.autre, label: t('deleteLostReasonAutre') },
          ]}
        />
      </FieldV2>
      {lostReason === LostReasonType.autre ? (
        <FieldV2 label={t('deleteOtherLostReasonLabel')} required>
          <InputV2
            value={otherLostReason}
            onChange={setOtherLostReason}
            placeholder={t('deleteOtherLostReasonPlaceholder')}
          />
        </FieldV2>
      ) : null}
      <FieldV2 label={t('deleteLabel')} required>
        <InputV2 value={confirmText} onChange={setConfirmText} placeholder="SUPPRIMER" />
      </FieldV2>
    </ModalV2>
  );
};

export default DeleteModal;
